import Flex from '@react-css/flex';
import { SettingsDescription, SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import useAlertQueue from 'hooks/useAlertQueue';
import { useBalanceBuyerPortal, useBalanceQualification } from 'hooks/useBalanceRedirect';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import { refreshMe } from 'store/slices/me';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import StatusChip from 'storybookConfig/stories/molecules/Chip/StatusChip';
import DSSpinner from 'storybookConfig/stories/molecules/Spinner';

const BalanceBuyerSettings = () => {
  const dispatch = useAppDispatch();
  const alertQueue = useAlertQueue();
  const company = useAppSelector(selectCompany);

  const { openPortal, isRedirecting: isOpeningPortal } = useBalanceBuyerPortal();
  const { openQualification, isRedirecting: isOpeningQualification } = useBalanceQualification();

  const verified = !!company?.balance?.paymentMethodVerified;
  const qualificationStatus = company?.balance?.qualification?.status;
  const approved = qualificationStatus === 'approved';
  const applied = qualificationStatus === 'pending' || qualificationStatus === 'pendingActivation';
  const inReview = qualificationStatus === 'inReview';
  const declined = qualificationStatus === 'declined';
  const inProgress = applied || inReview;

  const qualificationBadge = (() => {
    if (approved) return { label: 'Approved for Net Terms', kind: 'success' } as const;
    if (inReview) return { label: 'Awaiting Approval', kind: 'warning' } as const;
    if (applied) return { label: 'Applied for Net Terms', kind: 'info' } as const;
    if (declined) return { label: 'Net Terms Declined', kind: 'error' } as const;
    return null;
  })();

  // Balance onboarding and qualification happen on redirected pages, so refresh company state
  // when the tab regains focus — until both are settled, at which point the badges stop lying
  // about stale status.
  useEffect(() => {
    if (verified && approved) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatch(refreshMe());
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dispatch, verified, approved]);

  const onPaymentMethodClick = () => {
    openPortal().catch((err) => {
      alertQueue.addErrorAlert(
        'Something went wrong',
        (err as Error)?.message ?? 'Failed to open Balance portal'
      );
    });
  };

  const onQualificationClick = () => {
    openQualification().catch((err) => {
      alertQueue.addErrorAlert(
        'Something went wrong',
        (err as Error)?.message ?? 'Failed to start Balance qualification'
      );
    });
  };

  const paymentMethodLabel = verified ? 'Manage payment method' : 'Add payment method';

  let qualificationLabel = 'Apply for net terms';
  if (approved) qualificationLabel = 'Manage net terms';
  else if (inProgress) qualificationLabel = 'Continue net terms application';

  return (
    <>
      <SettingsSectionTitle>
        Balance
        <StatusChip kind={verified ? 'success' : 'pending'} className="ml-3">
          {verified ? 'Connected' : 'Not Connected'}
        </StatusChip>
        {qualificationBadge && (
          <StatusChip kind={qualificationBadge.kind} className="ml-2">
            {qualificationBadge.label}
          </StatusChip>
        )}
      </SettingsSectionTitle>
      <Flex column gap="6px">
        <SettingsDescription style={{ margin: 0 }}>
          {verified
            ? 'Your Balance payment method is connected. Manage your payment method through the Balance portal.'
            : 'Pay invoices via credit card or ACH with Balance. Add a payment method through the Balance portal.'}
        </SettingsDescription>
        <SettingsDescription style={{ margin: 0 }}>
          {approved
            ? 'You are approved for net terms with Balance. Manage your qualification through the Balance portal.'
            : 'Qualify to pay invoices on net terms with Balance. Complete your application through the Balance portal.'}
        </SettingsDescription>
      </Flex>
      <Flex gap="8px">
        <PrimaryButton size="small" onClick={onPaymentMethodClick} disabled={isOpeningPortal}>
          {isOpeningPortal ? <DSSpinner size="16px" color="bodyTextCaption" /> : paymentMethodLabel}
        </PrimaryButton>
        <PrimaryButton
          size="small"
          onClick={onQualificationClick}
          disabled={isOpeningQualification}
        >
          {isOpeningQualification ? (
            <DSSpinner size="16px" color="bodyTextCaption" />
          ) : (
            qualificationLabel
          )}
        </PrimaryButton>
      </Flex>
    </>
  );
};

export default BalanceBuyerSettings;
